import { type ReactNode, useRef, useState } from 'react'
import { InputGroup } from '@systemeio/components'
import { AtSignIcon, Link01Icon, LinkBroken02Icon, Send03Icon } from '@systemeio/icons'
import type { Extensions } from '@tiptap/core'
import type { MentionOptions } from '@tiptap/extension-mention'
import { EditorContent, useEditor, useEditorState } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import type { Emoji } from 'emoji-mart'
import { useTranslation } from 'next-i18next'
import { twJoin, twMerge } from 'tailwind-merge'
import useUser, { isAuthorizedUser } from 'shared/hooks/use-user'
import { usePostAttachmentsContext } from 'modules/attachments/components/context'
import EmbeddedVideoAttachment from 'modules/attachments/components/embedded-video-attachment'
import { GifAttachment } from 'modules/attachments/components/gif-attachment'
import AttachmentsPreview from 'modules/attachments/components/preview'
import { UploadAttachment } from 'modules/attachments/components/upload-attachment'
import { ToolbarButton } from 'modules/comments/components/comment-editor/common/toolbar-button'
import { EmojiPicker } from 'modules/comments/components/comment-editor/elements/emoji-picker/emoji-picker'
import { LinkModal } from 'modules/comments/components/comment-editor/elements/Link/link-modal'
import { EditorMode } from 'modules/comments/types'
import { docToHtml, htmlToDoc } from 'modules/comments/utils/comment-html'
import { isCommunityExtra } from 'modules/comments/utils/comments-extra-type-guards'
import { PostMention } from 'modules/community/components/editor/extensions/mention-extension'
import { useMentionSuggestion } from 'modules/community/components/editor/extensions/mention-suggestion'
import type { CommentEditorProps } from '.'
import { useCommentsContext } from '../comments-list'

export const createCommentExtensions = (
  mentionSuggestion?: MentionOptions['suggestion'],
): Extensions => [
  StarterKit.configure({
    heading: false,
    code: false,
    codeBlock: false,
    blockquote: false,
    horizontalRule: false,
    hardBreak: false,
    trailingNode: false,
    strike: false,
    bulletList: false,
    orderedList: false,
    listItem: false,
    listKeymap: false,
    link: {
      openOnClick: false,
      autolink: false,
      HTMLAttributes: { target: '_blank' },
    },
  }),
  PostMention.configure({
    HTMLAttributes: { class: 'mention' },
    suggestion: mentionSuggestion,
  }),
]

export type ToolbarPopup = 'gif' | 'emoji' | 'link' | 'video'

export const TiptapCommentEditor = ({
  handleAddComment,
  setActiveComment,
  isRoot,
  initialHtml,
  initialMentions,
  mode = EditorMode.Add,
  onCancel,
  className = '',
  editableComment,
}: CommentEditorProps): ReactNode => {
  const { user } = useUser()
  const { t } = useTranslation()
  const { extra } = useCommentsContext()
  const { attachments, isUploading } = usePostAttachmentsContext()
  const html = editableComment?.text ?? initialHtml ?? ''
  const mentions = editableComment?.mentions ?? initialMentions ?? []
  const [isFocused, setIsFocused] = useState(false)
  const [openPopup, setOpenPopup] = useState<ToolbarPopup | null>(null)
  const changePopup = (popup: ToolbarPopup, open: boolean): void => {
    setOpenPopup(current => (open ? popup : current === popup ? null : current))
  }
  const community = isCommunityExtra(extra) ? extra.community : undefined
  const communityRef = useRef(community)
  communityRef.current = community
  const mentionSuggestion = {
    ...useMentionSuggestion(() => communityRef.current),
    placement: 'top-start' as const,
  }

  const editor = useEditor({
    extensions: createCommentExtensions(mentionSuggestion),
    content: htmlToDoc(html, mentions),
    immediatelyRender: false,
    shouldRerenderOnTransaction: false,
    autofocus: !isRoot || mode === EditorMode.Edit ? 'end' : false,
    editorProps: {
      attributes: {
        dir: 'auto',
        class:
          'comment-input max-h-[max(30svh,5rem)] w-full overflow-y-auto pr-2 wrap-break-word whitespace-break-spaces text-start text-text-primary focus-visible:outline-hidden [&_a]:text-brand-primary [&_a]:no-underline [&_a:hover]:underline',
      },
    },
  })

  const linkActive =
    useEditorState({
      editor,
      selector: ({ editor: currentEditor }) =>
        currentEditor ? currentEditor.isActive('link') : false,
    }) ?? false
  const hasContentState = useEditorState({
    editor,
    selector: ({ editor: currentEditor }) => (currentEditor ? !currentEditor.isEmpty : false),
  })
  const hasContent = editor ? !editor.isEmpty : hasContentState
  const expanded = isFocused || openPopup !== null || hasContent || mode === EditorMode.Edit

  const cancel = (): void => {
    onCancel?.()
    setActiveComment(null)
  }

  if (!editor) return null

  const handleMentionClick = (): void => {
    editor.chain().focus().insertContent('@').run()
  }

  const handleAddEmoji = (emoji: typeof Emoji.Props): void => {
    editor.chain().focus().insertContent(emoji.native).run()
  }

  const handleInsertLink = (url: string): void => {
    if (editor.state.selection.empty) {
      editor
        .chain()
        .focus()
        .insertContent({
          type: 'text',
          text: url,
          marks: [{ type: 'link', attrs: { href: url, target: '_blank' } }],
        })
        .insertContent(' ')
        .run()
      return
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
    editor.commands.setTextSelection(editor.state.selection.to)
  }

  return (
    <div className={className}>
      <div className="flex flex-row items-start gap-2.5 sm:gap-2.5">
        {isAuthorizedUser(user) && (
          <img alt="avatar" className="mt-2 size-10 rounded-full" src={user.avatarUrl} />
        )}

        <InputGroup.Root
          onFocus={event => setIsFocused(event.currentTarget.contains(event.target))}
          onBlur={event => setIsFocused(event.currentTarget.contains(event.relatedTarget))}
          onClick={event => {
            if (event.target === event.currentTarget) editor.commands.focus('end')
          }}
          className={twMerge(
            'relative h-auto w-[calc(100%-40px)] min-w-0 grow-0 cursor-text flex-col items-stretch border border-transparent p-3 transition-all duration-200',
            expanded && 'p-5 pr-14 pb-12',
          )}
          onKeyDown={event => {
            if (!(event.target instanceof Node) || !event.currentTarget.contains(event.target))
              return
            if (event.key === 'Escape' && (!isRoot || mode === EditorMode.Edit)) cancel()
          }}
        >
          <div
            data-slot="input-group-control"
            data-focused={isFocused || undefined}
            className="pointer-events-none absolute size-0"
          />
          {!hasContent && (
            <span
              className={twJoin(
                'pointer-events-none absolute text-text-secondary/50',
                expanded ? 'top-5 left-5' : 'top-3 left-3',
              )}
            >
              {t('comments.components.comments.input_placeholder_label')}
            </span>
          )}
          <EditorContent editor={editor} />
          <AttachmentsPreview className={twJoin(!!attachments?.length && `pt-4`)} />

          <div
            className={twJoin(
              'absolute bottom-2.5 left-3.5 flex items-center gap-2 md:gap-3',
              !expanded && 'hidden',
            )}
            onClick={event => {
              event.stopPropagation()
            }}
          >
            <UploadAttachment
              className="group"
              iconClassName="size-5 text-text-secondary group-hover:text-text-primary md:size-6"
            />
            <EmbeddedVideoAttachment
              iconClassName="size-5 md:size-6"
              onOpenChange={open => changePopup('video', open)}
            />
            <GifAttachment
              iconClassName="size-5 text-text-secondary hover:text-text-primary md:size-6"
              onOpenChange={open => changePopup('gif', open)}
            />
            {openPopup !== 'link' && (
              <>
                <ToolbarButton
                  format={linkActive ? 'unlink' : 'link'}
                  active={linkActive}
                  onClick={() => {
                    if (linkActive) {
                      editor.chain().focus().extendMarkRange('link').unsetLink().run()
                      return
                    }
                    changePopup('link', true)
                  }}
                >
                  {linkActive ? (
                    <LinkBroken02Icon className="size-5 text-[initial] md:size-6" />
                  ) : (
                    <Link01Icon className="size-5 text-text-secondary md:size-6" />
                  )}
                </ToolbarButton>
                {community?.communityPath ? (
                  <ToolbarButton format="mention" onClick={handleMentionClick}>
                    <AtSignIcon className="size-5 text-text-secondary hover:text-[initial] md:size-6" />
                  </ToolbarButton>
                ) : null}
                <EmojiPicker
                  iconClassName="size-5 md:size-6"
                  onEmojiSelect={handleAddEmoji}
                  onOpenChange={open => changePopup('emoji', open)}
                />
              </>
            )}
            <LinkModal
              setLinkIsEditing={open => changePopup('link', open)}
              linkIsEditing={openPopup === 'link'}
              onInsertLink={handleInsertLink}
            />
          </div>
          {expanded && (
            <>
              {mode !== EditorMode.Edit && !isRoot && (
                <button
                  className="text-brand-primary hover:text-brand-primary-hover absolute right-3.5 bottom-2.5 block cursor-pointer text-sm md:hidden"
                  onClick={cancel}
                >
                  {t('comments.components.comments.cancel_label')}
                </button>
              )}
              <button
                className="text-brand-primary absolute top-5 right-6 flex size-6 items-center justify-center disabled:text-text-secondary"
                onClick={() => {
                  handleAddComment(docToHtml(editor.getJSON()), attachments)
                  if (mode === EditorMode.Edit) return
                  editor.commands.clearContent(true)
                }}
                disabled={!hasContent || isUploading}
              >
                <Send03Icon className="size-5 md:size-6" />
              </button>
            </>
          )}
        </InputGroup.Root>
      </div>
      {(!isRoot || mode === EditorMode.Edit) && (
        <button
          onClick={cancel}
          className="ml-12 hidden cursor-pointer pt-2.5 text-sm text-text-primary/50 transition-colors hover:text-text-primary md:inline-block"
          dangerouslySetInnerHTML={{
            __html: t('comments.components.comments.cancel_label_or_esc'),
          }}
        />
      )}
      {mode === EditorMode.Edit && (
        <div className="mt-2.5 flex items-center justify-end">
          <button
            className="bg-brand-primary text-background-secondary z-1 flex min-h-9 w-auto min-w-20 cursor-pointer items-center justify-end self-center rounded-sm px-5 text-center text-sm font-bold outline-0 select-none md:hidden"
            onClick={cancel}
          >
            {t('comments.components.comments.cancel_label')}
          </button>
        </div>
      )}
    </div>
  )
}
